import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Plus, Trash2, Search, Building2, UserCheck, Calendar, DollarSign, Check } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { Collab, Deliverable, Client, Influencer, collabsRef, clientsRef, influencersRef, createDocument, getDocuments, updateDocument, auth } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const CONTENT_TYPES = [
  { value: 'post_instagram', label: 'Post Instagram' },
  { value: 'story_instagram', label: 'Story Instagram' },
  { value: 'reel_instagram', label: 'Réel Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'youtube', label: 'Intégration YouTube' },
  { value: 'twitch', label: 'Live Twitch' },
  { value: 'story_snapchat', label: 'Story Snapchat' },
] as const;

const COLLAB_TYPES = [
  { value: 'collaboration_entrante', label: 'Collaboration entrante' },
  { value: 'collaboration_sortante', label: 'Collaboration sortante' },
  { value: 'casting_interne', label: 'Casting interne' },
  { value: 'casting_externe', label: 'Casting externe' },
] as const;

export default function CollaborationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [clients, setClients] = useState<Client[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isClientSelectorOpen, setIsClientSelectorOpen] = useState<boolean>(false);
  const [isInfluencerSelectorOpen, setIsInfluencerSelectorOpen] = useState<boolean>(false);
  const [clientSearch, setClientSearch] = useState('');
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Partial<Collab>>({
    name: '',
    type: 'collaboration_entrante',
    brand: '',
    date: new Date(),
    firstContactDate: new Date(), 
    signedDate: new Date(), // Set today's date as default
    influencers: [],
    influencerIds: [],
    linkedPublicInfluencerIds: [],
    influencerIds: [],
    quote_sent: false,
    contract_signed: false,
    casting_validated: false,
    manager: '',
    post_progress: '0',
    billing_status: 'pending',
    payment_received: false,
    invoice_sent: false,
    budget: 0,
    quote_number: '',
    invoice_number: '',
  });

  useEffect(() => {
    loadInitialData();
  }, [id, location]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      // Check if we have an id in the query params (from edit button in details page)
      const urlParams = new URLSearchParams(window.location.search);
      const queryId = urlParams.get('id');
      const collaborationId = queryId || id;
      
      // Load clients and influencers
      const [clientsData, influencersData] = await Promise.all([
        getDocuments<Client>(clientsRef),
        getDocuments<Influencer>(influencersRef),
      ]);
      
      setClients(clientsData);
      setInfluencers(influencersData.filter(inf => !inf.archived));
      
      // If editing existing collaboration, load its data
      if (collaborationId && collaborationId !== 'new') {
        const collabDoc = await getDoc(doc(db, 'collabs', collaborationId));
        if (collabDoc.exists()) {
          const collabData = { id: collabDoc.id, ...collabDoc.data() } as Collab;
          setFormData(collabData);
        } else {
          navigate('/app/collaborations');
        }
      }
      
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    console.log("Soumission du formulaire", formData);
    console.log("Soumission du formulaire", formData);
    
    try {
      setIsSaving(true);
      
      // Calculer l'ID de la collaboration à partir de l'URL ou du paramètre
      const collaborationId = new URLSearchParams(window.location.search).get('id') || id;
      
      console.log(`Sauvegarde de la collaboration: ${collaborationId || 'nouvelle'}`);
      
      // Créer une copie profonde des données du formulaire pour éviter les références partagées
      const formDataToSave = JSON.parse(JSON.stringify(formData));
      
      // Récupérer le nom de l'agence pour l'inclure dans les données
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.agencyId) {
            try {
              const agencyDoc = await getDoc(doc(db, 'agencies', userData.agencyId));
              if (agencyDoc.exists()) {
                formDataToSave.agencyName = agencyDoc.data().name;
              }
            } catch (agencyError) {
              console.warn('Impossible de récupérer le nom de l\'agence:', agencyError);
              // Continue sans le nom de l'agence
            }
          }
        }
      }
      
      // Validate required fields
      if (!formData.name || !formData.brand || !formData.manager) {
        setError('Veuillez remplir tous les champs obligatoires');
        setIsSaving(false);
        return;
      }

      // Préparer les IDs des influenceurs
      const influencerIds = formData.influencers?.map(inf => inf.id).filter(Boolean) as string[];
      formDataToSave.influencerIds = influencerIds;
      
      // Récupérer les influenceurs qui ont un lien public activé
      const linkedPublicInfluencerIds: string[] = [];
      if (influencerIds.length > 0) {
        for (const id of influencerIds) {
          if (!id) continue;
          try {
            const influencerDoc = await getDoc(doc(db, 'influencers', id));
            if (influencerDoc.exists() && influencerDoc.data().publicLinkEnabled) {
              linkedPublicInfluencerIds.push(id);
            }
          } catch (error) {
            console.warn(`Impossible de vérifier le statut public de l'influenceur ${id}:`, error);
          }
        }
      }
      formDataToSave.linkedPublicInfluencerIds = linkedPublicInfluencerIds;
      
      // Ensure influencerIds is properly set
      if (formData.influencers && formData.influencers.length > 0) {
        const influencerIds = formData.influencers
          .filter(inf => inf.id) // Only include influencers with IDs
          .map(inf => inf.id as string);
        
        formDataToSave.influencerIds = influencerIds;
      }
      
      // Calculate budget from influencers if not set
      if (!formData.budget) {
        const calculatedBudget = formData.influencers?.reduce((total, influencer) => {
          return total + (parseFloat(influencer.rate) || 0);
        }, 0) || 0;
        
        formDataToSave.budget = calculatedBudget;
      }
      
      if (collaborationId && collaborationId !== 'new') {
        console.log("Mise à jour de la collaboration existante:", collaborationId);
        // Update existing collaboration
        await updateDocument(collabsRef, collaborationId, {
          ...formDataToSave, 
          createdBy: formDataToSave.createdBy || auth.currentUser?.uid,
          updatedAt: new Date(),
        });
        
        setSuccess('Collaboration mise à jour avec succès');
        
        // Rediriger vers la page de détails après la mise à jour
        setTimeout(() => {
          navigate(`/app/collaborations/${collaborationId}`, { replace: true });
        }, 1500);
      } else {
        console.log("Création d'une nouvelle collaboration");
        // Create new collaboration
        try {
          const newCollab = await createDocument(collabsRef, {
            ...formDataToSave,
            createdBy: auth.currentUser?.uid,
            createdAt: new Date(),
            updatedAt: new Date(),
          } as Collab);
          
          setSuccess('Collaboration créée avec succès');
          
          // Redirect to the new collaboration page
          if (newCollab.id) {
            setTimeout(() => {
              navigate(`/app/collaborations/${newCollab.id}`);
            }, 1500);
          }
        } catch (createError) {
          console.error("Erreur lors de la création:", createError);
          throw createError;
        }
        
        setSuccess('Collaboration créée avec succès');
      }
    } catch (error) {
      console.error('Error saving collaboration:', error);
      setError('Erreur lors de la sauvegarde de la collaboration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectClient = (client: Client) => {
    setFormData(prev => ({
      ...prev,
      brand: client.brand_name,
    }));
    setIsClientSelectorOpen(false);
  };

  const handleSelectInfluencer = (influencer: Influencer) => {
    setFormData(prev => ({
      ...prev,
      influencerIds: [...(prev.influencerIds || []), influencer.id!],
      influencers: [
        ...(prev.influencers || []),
        {
          id: influencer.id,
          id: influencer.id,
          fullName: influencer.fullName,
          deliverables: [],
          rate: '0',
          creator_invoice_sent: false,
          paidPercentage: 0
        },
      ],
      influencerIds: [...(prev.influencerIds || []), influencer.id!]
    }));
    setIsInfluencerSelectorOpen(false);
  };

  const handleAddDeliverable = (influencerIndex: number) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]) {
        newInfluencers[influencerIndex] = {
          ...newInfluencers[influencerIndex],
          deliverables: [
            ...(newInfluencers[influencerIndex].deliverables || []),
            { type: '', description: '', price: '0' }
          ]
        };
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const handleRemoveDeliverable = (influencerIndex: number, deliverableIndex: number) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]) {
        newInfluencers[influencerIndex] = {
          ...newInfluencers[influencerIndex],
          deliverables: newInfluencers[influencerIndex].deliverables?.filter((_, i) => i !== deliverableIndex) || []
        };
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const handleDeliverableChange = (influencerIndex: number, deliverableIndex: number, field: string, value: string) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]?.deliverables?.[deliverableIndex]) {
        newInfluencers[influencerIndex].deliverables[deliverableIndex] = {
          ...newInfluencers[influencerIndex].deliverables[deliverableIndex],
          [field]: value
        };
        
        // Update rate based on deliverables
        if (field === 'price') {
          const totalPrice = newInfluencers[influencerIndex].deliverables.reduce((sum, d) => {
            return sum + parseFloat(d.price || '0');
          }, 0);
          
          newInfluencers[influencerIndex].rate = totalPrice.toString();
        }
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const handleRemoveInfluencer = (index: number) => {
    const influencerId = formData.influencers?.[index]?.id;
    setFormData(prev => ({
      ...prev,
      influencers: prev.influencers?.filter((_, i) => i !== index) || [],
      influencerIds: prev.influencerIds?.filter(id => id !== influencerId) || []
    }));
  };

  const calculateTotalRate = (deliverables: Deliverable[]) => {
    return deliverables.reduce((total, deliverable) => {
      const price = parseFloat(deliverable.price) || 0;
      return total + price;
    }, 0).toString();
  };

  const updateInfluencerRate = (influencerIndex: number) => {
    setFormData(prev => {
      const newInfluencers = [...(prev.influencers || [])];
      if (newInfluencers[influencerIndex]) {
        const totalRate = calculateTotalRate(newInfluencers[influencerIndex].deliverables || []);
        newInfluencers[influencerIndex].rate = totalRate;
      }
      return { ...prev, influencers: newInfluencers };
    });
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return '';
    
    if (typeof date === 'string') {
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      const d = new Date(date.seconds * 1000);
      return d.toISOString().split('T')[0];
    }
    
    return new Date(date).toISOString().split('T')[0];
  };

  const filteredClients = clients.filter(client =>
    client.brand_name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredInfluencers = influencers.filter(influencer =>
    influencer.fullName?.toLowerCase().includes(influencerSearch.toLowerCase()) ||
    influencer.username?.toLowerCase().includes(influencerSearch.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/collaborations')}
            className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-dark-200' : 'hover:bg-gray-100'} transition-colors`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {id && id !== 'new' ? 'Modifier la collaboration' : 'Nouvelle collaboration'}
            </h1>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              {id && id !== 'new' ? 'Mettre à jour les détails de la collaboration' : 'Créer une nouvelle collaboration'}
            </p>
          </div>
        </div>
        <button
          onClick={handleSubmit}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      {error && (
        <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-red-900/50 border-red-500/20 text-red-200' : 'bg-red-50 border-red-200 text-red-800'} border`}>
          {error}
        </div>
      )}

      {success && (
        <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-green-900/50 border-green-500/20 text-green-200' : 'bg-green-50 border-green-200 text-green-800'} border`}>
          {success}
        </div>
      )}

      <form className="space-y-8">
        {/* Informations de base */}
        <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
            Informations de base
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                Nom de la collaboration
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full"
                placeholder="Ex: Campagne été 2024"
                required
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                Type de collaboration
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as Collab['type'] })}
                className="w-full"
                required
              >
                {COLLAB_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                Marque
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.brand}
                  className="w-full pr-10"
                  required
                  readOnly
                  onClick={() => setIsClientSelectorOpen(true)}
                  placeholder="Sélectionner un client"
                />
                <button
                  type="button"
                  onClick={() => setIsClientSelectorOpen(true)}
                  className={`absolute inset-y-0 right-0 px-3 flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                Budget total (€)
              </label>
              <input
                type="number"
                value={formData.budget || ''}
                onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                className="w-full"
                placeholder="Budget total de la campagne"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                Date de premier contact
              </label>
              <input
                type="date"
                value={formData.firstContactDate ? formatDate(formData.firstContactDate) : ''}
                onChange={(e) => setFormData({ ...formData, firstContactDate: new Date(e.target.value) })}
                className="w-full"
                required
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                Date de signature
              </label>
              <input
                type="date"
                value={formData.signedDate ? formatDate(formData.signedDate) : formatDate(new Date())}
                onChange={(e) => setFormData({ ...formData, signedDate: new Date(e.target.value) })}
                className="w-full"
              />
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                Chef de projet
              </label>
              <input
                type="text"
                value={formData.manager}
                onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                className="w-full"
                required
              />
            </div>
          </div>
        </div>
        
        {/* Influenceurs */}
        <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Influenceurs
            </h2>
            <button
              type="button"
              onClick={() => setIsInfluencerSelectorOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
            >
              <Plus className="w-4 h-4" />
              Ajouter un influenceur
            </button>
          </div>
          
          <div className="space-y-6">
            {formData.influencers?.map((influencer, influencerIndex) => (
              <div 
                key={influencerIndex} 
                className={`${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'} rounded-lg p-6 border ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${theme === 'dark' ? 'bg-accent-blue/20' : 'bg-accent-blue/10'} flex items-center justify-center`}>
                      <UserCheck className="w-5 h-5 text-accent-blue" />
                    </div>
                    <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {influencer.fullName}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveInfluencer(influencerIndex)}
                    className="text-red-500 hover:text-red-400"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                      Tarif global
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={influencer.rate || 0}
                        onChange={(e) => {
                          const newInfluencers = [...(formData.influencers || [])];
                          newInfluencers[influencerIndex] = {
                            ...newInfluencers[influencerIndex],
                            rate: e.target.value
                          };
                          setFormData({ ...formData, influencers: newInfluencers });
                        }}
                        className="w-full"
                        placeholder="0"
                      />
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>€</span>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                        Livrables
                      </label>
                      <button
                        type="button"
                        onClick={() => handleAddDeliverable(influencerIndex)}
                        className={`text-sm ${theme === 'dark' ? 'text-accent-blue hover:text-accent-blue/80' : 'text-indigo-600 hover:text-indigo-500'}`}
                      >
                        + Ajouter un livrable
                      </button>
                    </div>
                    
                    <div className={`space-y-3 ${theme === 'dark' ? 'bg-dark-200' : 'bg-white'} rounded-lg p-4 border ${theme === 'dark' ? 'border-dark-300' : 'border-gray-200'}`}>
                      {influencer.deliverables?.map((deliverable, deliverableIndex) => (
                        <div key={deliverableIndex} className="grid grid-cols-3 gap-3">
                          <select
                            value={deliverable.type}
                            onChange={(e) => handleDeliverableChange(influencerIndex, deliverableIndex, 'type', e.target.value)}
                            className="w-full"
                          >
                            <option value="">Sélectionner un type</option>
                            {CONTENT_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={deliverable.description}
                            onChange={(e) => handleDeliverableChange(influencerIndex, deliverableIndex, 'description', e.target.value)}
                            className="w-full"
                            placeholder="Description"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={deliverable.price || 0}
                              onChange={(e) => handleDeliverableChange(influencerIndex, deliverableIndex, 'price', e.target.value)}
                              className="w-full"
                              placeholder="0"
                            />
                            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>€</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveDeliverable(influencerIndex, deliverableIndex)}
                              className="text-red-500 hover:text-red-400 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {(!influencer.deliverables || influencer.deliverables.length === 0) && (
                        <div className={`text-center py-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Aucun livrable ajouté
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const newInfluencers = [...(formData.influencers || [])];
                        newInfluencers[influencerIndex] = {
                          ...newInfluencers[influencerIndex],
                          creator_invoice_sent: !newInfluencers[influencerIndex].creator_invoice_sent
                        };
                        setFormData({ ...formData, influencers: newInfluencers });
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                        influencer.creator_invoice_sent 
                          ? 'bg-green-500/20 text-green-300 border border-green-500/20' 
                          : theme === 'dark' 
                            ? 'bg-dark-200 text-gray-400 border border-dark-300 hover:bg-dark-300' 
                            : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {influencer.creator_invoice_sent && <Check className="w-4 h-4" />}
                      <span className="text-sm">Facture créateur reçue</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {(!formData.influencers || formData.influencers.length === 0) && (
              <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun influenceur ajouté à cette collaboration</p>
                <p className="text-sm mt-1">Cliquez sur "Ajouter un influenceur" pour commencer</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Statut et facturation */}
        <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
            Statut et facturation
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, quote_sent: !formData.quote_sent })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                      formData.quote_sent 
                        ? 'bg-green-500/20 text-green-300 border border-green-500/20' 
                        : theme === 'dark' 
                          ? 'bg-dark-200 text-gray-400 border border-dark-300 hover:bg-dark-300' 
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {formData.quote_sent && <Check className="w-4 h-4" />}
                    <span className="text-sm">Devis envoyé</span>
                  </button>
                </label>
                {formData.quote_sent && (
                  <input
                    type="text"
                    value={formData.quote_number}
                    onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })}
                    placeholder="Numéro de devis"
                    className="flex-1"
                  />
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, contract_signed: !formData.contract_signed })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                      formData.contract_signed 
                        ? 'bg-green-500/20 text-green-300 border border-green-500/20' 
                        : theme === 'dark' 
                          ? 'bg-dark-200 text-gray-400 border border-dark-300 hover:bg-dark-300' 
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {formData.contract_signed && <Check className="w-4 h-4" />}
                    <span className="text-sm">Contrat signé</span>
                  </button>
                </label>
              </div>

            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, invoice_sent: !formData.invoice_sent })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                      formData.invoice_sent 
                        ? 'bg-green-500/20 text-green-300 border border-green-500/20' 
                        : theme === 'dark' 
                          ? 'bg-dark-200 text-gray-400 border border-dark-300 hover:bg-dark-300' 
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {formData.invoice_sent && <Check className="w-4 h-4" />}
                    <span className="text-sm">Facture envoyée</span>
                  </button>
                </label>
                {formData.invoice_sent && (
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="Numéro de facture"
                    className="flex-1"
                  />
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, payment_received: !formData.payment_received })}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                      formData.payment_received 
                        ? 'bg-green-500/20 text-green-300 border border-green-500/20' 
                        : theme === 'dark' 
                          ? 'bg-dark-200 text-gray-400 border border-dark-300 hover:bg-dark-300' 
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {formData.payment_received && <Check className="w-4 h-4" />}
                    <span className="text-sm">Paiement reçu</span>
                  </button>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/app/collaborations')}
            className={`px-4 py-2 ${theme === 'dark' ? 'bg-dark-200 text-gray-300 hover:bg-dark-300' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-lg transition-colors`}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* Client Selector Modal */}
      <Dialog
        open={isClientSelectorOpen}
        onClose={() => {
          setIsClientSelectorOpen(false); 
        }}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-2xl w-full mx-4 p-6 border shadow-lg`}>
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsClientSelectorOpen(false)}
                className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-dark-200 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Sélectionner un client
              </h2>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="w-full pl-10"
                />
              </div>

              <div className={`space-y-2 max-h-96 overflow-y-auto ${theme === 'dark' ? 'scrollbar-dark' : 'scrollbar-light'}`}>
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectClient(client);
                    }}
                    className={`w-full flex items-center p-4 ${theme === 'dark' ? 'bg-dark-100 hover:bg-dark-200' : 'bg-gray-50 hover:bg-gray-100'} rounded-lg transition-colors cursor-pointer`}
                  >
                    <div className={`w-10 h-10 rounded-full ${theme === 'dark' ? 'bg-dark-200' : 'bg-white'} flex items-center justify-center mr-3`}>
                      <Building2 className="w-5 h-5 text-accent-blue" />
                    </div>
                    <div className="text-left">
                      <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {client.brand_name}
                      </h3>
                      {client.contacts[0] && (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {client.contacts[0].name} • {client.contacts[0].email}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                
                {filteredClients.length === 0 && (
                  <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun client trouvé</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Influencer Selector Modal */}
      <Dialog
        open={isInfluencerSelectorOpen}
        onClose={() => {
          setIsInfluencerSelectorOpen(false); 
        }}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-2xl w-full mx-4 p-6 border shadow-lg`}>
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsInfluencerSelectorOpen(false)}
                className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-dark-200 text-gray-400 hover:text-white' : 'bg-gray-100 text-gray-600 hover:text-gray-900'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Sélectionner un influenceur
              </h2>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={influencerSearch}
                  onChange={(e) => setInfluencerSearch(e.target.value)}
                  placeholder="Rechercher un influenceur..."
                  className="w-full pl-10"
                />
              </div>

              <div className={`space-y-2 max-h-96 overflow-y-auto ${theme === 'dark' ? 'scrollbar-dark' : 'scrollbar-light'}`}>
                {filteredInfluencers.map((influencer) => (
                  <div
                    key={influencer.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectInfluencer(influencer);
                    }}
                    className={`w-full flex items-center p-4 ${theme === 'dark' ? 'bg-dark-100 hover:bg-dark-200' : 'bg-gray-50 hover:bg-gray-100'} rounded-lg transition-colors cursor-pointer`}
                  >
                    <div className={`w-10 h-10 rounded-full ${theme === 'dark' ? 'bg-dark-200' : 'bg-white'} flex items-center justify-center mr-3`}>
                      <UserCheck className="w-5 h-5 text-accent-blue" />
                    </div>
                    <div className="text-left">
                      <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {influencer.fullName}
                      </h3>
                      <div className={`flex items-center gap-2 mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {influencer.platforms.map((platform, index) => (
                          <span key={index}>
                            {platform.name} • {platform.audience}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
                
                {filteredInfluencers.length === 0 && (
                  <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun influenceur trouvé</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}